define((require) => {
    const AbstractWidgetModel = require("shared/ui/experience/widgets/AbstractWidgetModel");
    const WidgetTypes         = require("shared/ui/experience/widgets/WidgetTypes");
    const {defaults, pluck}   = require("underscore");

    /**
     * The model for keeping track of widget selection in The Editor.
     *
     * Consumers of this class should prefer specific methods,
     * e.g. `.select(widgets)` or `.clear()`, rather than calling `.set("widgets", ...)`.
     *
     * @author Yaroslav <@yil>
     * @class WidgetSelectionViewModel
     */
    return AbstractWidgetModel.extend({
        _editorViewModel: null,

        defaults: defaults({
            widgets: []  // Array of widget views
        }, AbstractWidgetModel.prototype.defaults),

        computed: {
            models: {
                deps: ["widgets"],
                value(widgets) {
                    if (widgets && !widgets.every((element) => typeof element === "undefined")) {
                        return pluck(widgets, "model");
                    }
                    return null;
                }
            }
        },

        isEmpty() {
            return this.get("widgets").length === 0;
        },

        isSingle() {
            return this.get("widgets").length === 1;
        },

        isMultiple() {
            return this.get("widgets").length > 1;
        },

        clear() {
            this.set("widgets", []);
        },

        select(widgets) {
            this.set("widgets", widgets);
        },

        append(selectedWidgets) {
            this.set("widgets", this.get("widgets").concat(selectedWidgets));
            if (selectedWidgets.length === 1) {
                selectedWidgets[0].model.set("isSelected", true);
            }
        },

        remove(widget) {
            this.set("widgets", this.get("widgets").filter((selectedWidget) => {
                return selectedWidget !== widget;
            }));
        },

        initialize(attrs, options) {
            this.__super(attrs, options);
            this._editorViewModel = options.editorViewModel;
            this.listenTo(this, "change:widgets", this._onNewWidgetsSelected);
            this.listenTo(this, "change:left change:top", this._onLeftOrTopChanged);
        },

        /**
         * Updates positions of containing widgets effectively moving all containing widgets
         * simultaneously by given `amount` (in absolute values) on give axis (either 'x' or 'y').
         *
         * @param {'x'|'y'} axis to update
         * @param {Number} amount in absolute values (pixels)
         */
        moveBy(axis, amount) {
            this.get("widgets").forEach(({model, viewModel}) => {
                const experience = viewModel.get("experience");
                const bounds = {
                    x: model.get("left"),
                    y: model.get("top")
                };
                const side = axis === "x" ? "left" : "top";
                const offset = experience.getPercentagePosition({[side]: Math.abs(amount)});

                bounds[axis] += Math.sign(amount) * offset[side];

                model.set({
                    left: bounds.x,
                    top:  bounds.y
                }, {render: true});
            });
        },

        _onNewWidgetsSelected() {
            this.previous("widgets").forEach(({model}) => this.stopListening(model));
            this.get("widgets").forEach(({model}) => this.listenTo(model, "change:left change:top", this._recalcCoords));

            this._recalcCoords();
        },

        /**
         * Listener for `left` and `top` attributes changes (of this model).
         * Calculates offset with the previous value and moves all the containing widgets (if any) by that difference.
         * @param {WidgetSelectionViewModel} model this model
         * @param {Number} value new value for either `left` or `top`
         * @param {any} options any changes supplied to the `.set()` upon changin the value
         */
        _onLeftOrTopChanged(model, value, {stickitChange}) {
            if (!stickitChange) {
                // ensure this method processes only updates from view, and not from itself
                return;
            }

            const side = stickitChange.observe;
            const diff = this.get(side) - this.previous(side);
            this.get("widgets").forEach(({model}) => model.set(side, model.get(side) + diff));
        },

        _recalcCoords() {
            if (this.isEmpty()) {
                this.set({left: 0, top: 0});
            } else {
                this.set({
                    left: this._getLeftmostCoord(),
                    top: this._getTopmostCoord()
                });
            }
        },

        // These two methods need to be adjusted for the case where we've selected
        // a subwidget in a dynamic product widget. Leftmost and topmost coordinate need
        // to add the left/top of the parent widget instead of the subwidget.
        _getLeftmostCoord() {
            return Math.min(...this.get("widgets").map((widget) => {
                if (widget.parentView.model && widget.parentView.model.get("type") === WidgetTypes.DYNAMIC_PRODUCT.id) {
                    return widget.model.get("left") + widget.parentView.model.get("left");
                }
                return widget.model.get("left");
            }));
        },

        _getTopmostCoord() {
            return Math.min(...this.get("widgets").map((widget) => {
                if (widget.parentView.model && widget.parentView.model.get("type") === WidgetTypes.DYNAMIC_PRODUCT.id) {
                    return widget.model.get("top") + widget.parentView.model.get("top");
                }
                return widget.model.get("top");
            }));
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/WidgetSelectionViewModel.js